import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckCircle2, Award, ArrowRight, Palette, Crown, Diamond } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface FeaturedArtist {
  id: string;
  user_id: string;
  display_name: string | null;
  username: string | null;
  avatar_url: string | null;
  bio: string | null;
  id_verified: boolean;
  profile_picture_verified: boolean;
  artwork_count: number;
}

const FeaturedArtists = () => {
  const [artists, setArtists] = useState<FeaturedArtist[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchFeaturedArtists = async () => {
      try {
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('id, user_id, display_name, username, avatar_url, bio, id_verified, profile_picture_verified')
          .eq('user_type', 'seller')
          .eq('id_verified', true)
          .limit(10);

        if (profilesError) throw profilesError;

        if (!profiles || profiles.length === 0) {
          setArtists([]);
          setLoading(false);
          return;
        }

        const artistsWithCounts = await Promise.all(
          profiles.map(async (profile) => {
            const { count } = await supabase
              .from('artworks')
              .select('*', { count: 'exact', head: true })
              .eq('user_id', profile.user_id)
              .eq('status', 'approved');

            return {
              ...profile,
              artwork_count: count || 0,
            };
          })
        );

        const sortedArtists = artistsWithCounts
          .sort((a, b) => b.artwork_count - a.artwork_count)
          .slice(0, 6);

        setArtists(sortedArtists);
      } catch (error) {
        console.error('Error fetching featured artists:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedArtists();
  }, []);

  const getInitials = (name: string | null, username: string | null) => {
    if (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    if (username) {
      return username.slice(0, 2).toUpperCase();
    }
    return 'AR';
  };

  const getRankIcon = (index: number) => {
    if (index === 0) return <Crown className="h-4 w-4" />;
    if (index === 1) return <Diamond className="h-4 w-4" />;
    if (index === 2) return <Award className="h-4 w-4" />;
    return null;
  };

  if (loading) {
    return (
      <section className="py-28 px-4 bg-muted/20">
        <div className="container mx-auto">
          <div className="text-center mb-20">
            <Skeleton className="h-4 w-40 mx-auto mb-6" />
            <Skeleton className="h-14 w-80 mx-auto" />
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="p-8">
                <div className="flex items-center gap-5 mb-6">
                  <Skeleton className="h-20 w-20 rounded-full" />
                  <div className="flex-1">
                    <Skeleton className="h-6 w-36 mb-3" />
                    <Skeleton className="h-4 w-28" />
                  </div>
                </div>
                <Skeleton className="h-20 w-full" />
              </Card>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (artists.length === 0) {
    return null;
  }

  return (
    <section className="py-28 px-4 bg-muted/20">
      <div className="container mx-auto">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-3 mb-6 px-6 py-3 glass rounded-full luxury-border">
            <Award className="h-4 w-4 text-primary" />
            <span className="text-elegant text-xs text-primary tracking-luxury">Verified Excellence</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-medium text-foreground mb-6 luxury-divider pb-8">
            Featured Artists
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto font-light">
            Discover exceptional talent from our community of verified artists and collectors
          </p>
        </div>

        {/* Artists Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {artists.map((artist, index) => (
            <Card 
              key={artist.id}
              className="group relative p-8 hover:shadow-strong transition-all duration-700 bg-card border-border/30 overflow-hidden cursor-pointer hover-lift"
              onClick={() => navigate(`/profile/${artist.user_id}`)}
            >
              {/* Gradient overlay on hover */}
              <div className="absolute inset-0 bg-gradient-radial opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              
              {/* Rank badge for top 3 */}
              {index < 3 && (
                <div className="absolute top-6 right-6 z-10">
                  <Badge 
                    className={cn(
                      "flex items-center gap-1.5 px-3 py-1",
                      index === 0 && "bg-gradient-gold text-primary-foreground shadow-gold",
                      index === 1 && "bg-gradient-pearl text-foreground",
                      index === 2 && "bg-gradient-accent text-accent-foreground"
                    )}
                  >
                    {getRankIcon(index)}
                    #{index + 1}
                  </Badge>
                </div>
              )}

              <div className="relative z-10">
                {/* Artist avatar and info */}
                <div className="flex items-center gap-5 mb-6">
                  <div className="relative">
                    <Avatar className="h-20 w-20 border-2 border-primary/20 group-hover:border-primary/50 transition-all duration-500 shadow-soft group-hover:shadow-gold">
                      <AvatarImage src={artist.avatar_url || undefined} alt={artist.display_name || artist.username || 'Artist'} />
                      <AvatarFallback className="bg-gradient-hero text-primary-foreground text-xl font-display font-semibold">
                        {getInitials(artist.display_name, artist.username)}
                      </AvatarFallback>
                    </Avatar>
                    {/* Verified badge */}
                    <div className="absolute -bottom-1 -right-1 bg-gradient-hero rounded-full p-1.5 shadow-gold">
                      <CheckCircle2 className="h-4 w-4 text-primary-foreground" />
                    </div>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display text-xl font-medium truncate group-hover:text-primary transition-colors duration-500">
                      {artist.display_name || artist.username || 'Artist'}
                    </h3>
                    {artist.username && artist.display_name && (
                      <p className="text-sm text-muted-foreground truncate font-light">@{artist.username}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                      <Palette className="h-4 w-4 text-primary/60" />
                      <span className="font-light">{artist.artwork_count} artwork{artist.artwork_count !== 1 ? 's' : ''}</span>
                    </div>
                  </div>
                </div>

                {/* Bio */}
                {artist.bio && (
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-6 font-light leading-relaxed">
                    {artist.bio}
                  </p>
                )}

                {/* Verification badges */}
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className="text-xs bg-primary/5 border-primary/20 font-normal">
                    <CheckCircle2 className="h-3 w-3 mr-1.5 text-primary" />
                    ID Verified
                  </Badge>
                  {artist.profile_picture_verified && (
                    <Badge variant="outline" className="text-xs bg-accent/5 border-accent/20 font-normal">
                      <CheckCircle2 className="h-3 w-3 mr-1.5 text-accent" />
                      Photo Verified
                    </Badge>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* View all button */}
        <div className="text-center mt-16">
          <Button 
            size="lg" 
            variant="outline" 
            onClick={() => navigate("/gallery")}
            className="luxury-border hover:bg-primary/5 transition-all duration-500 group font-medium tracking-wide px-10 py-6"
          >
            Discover More Artists
            <ArrowRight className="ml-3 h-5 w-5 transition-transform duration-500 group-hover:translate-x-2" />
          </Button>
        </div>
      </div>
    </section>
  );
};

// Helper function for className merging
const cn = (...classes: (string | boolean | undefined)[]) => classes.filter(Boolean).join(' ');

export default FeaturedArtists;